/**
 * AreaSelector — Grid/list of laboratory areas with pending count badges.
 *
 * Displays all areas with their pending exam counts.
 * Selected area is highlighted visually.
 * "All areas" option clears the filter.
 *
 * Requirements: 6.1, 6.6
 */

import { useTranslation } from "react-i18next";
import { Layers } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { AreaLaboratorio, ContadorArea } from "@/hooks/use-procesamiento";
import { getAreaLabel, sortAreasByCount, getTotalPendientes } from "../utils/area-utils";

interface AreaSelectorProps {
  contadores: ContadorArea[];
  selectedArea: AreaLaboratorio | "";
  onAreaChange: (area: AreaLaboratorio | "") => void;
}

export function AreaSelector({ contadores, selectedArea, onAreaChange }: AreaSelectorProps) {
  const { t } = useTranslation();

  const sortedAreas = sortAreasByCount(contadores);
  const totalPendientes = getTotalPendientes(contadores);

  return (
    <div className="flex flex-wrap gap-2">
      {/* All areas button */}
      <button
        type="button"
        onClick={() => onAreaChange("")}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          selectedArea === ""
            ? "border-primary bg-primary/10 text-primary"
            : "border-border bg-background text-foreground"
        )}
      >
        <Layers className="h-4 w-4" />
        <span>{t("procesamiento.areas.todas")}</span>
        <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs">
          {totalPendientes}
        </Badge>
      </button>

      {/* Individual area buttons */}
      {sortedAreas.map((contador) => (
        <button
          key={contador.area}
          type="button"
          onClick={() => onAreaChange(contador.area)}
          className={cn(
            "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            selectedArea === contador.area
              ? "border-primary bg-primary/10 text-primary"
              : "border-border bg-background text-foreground"
          )}
        >
          <span>{getAreaLabel(contador.area)}</span>
          <Badge
            variant={contador.conteo > 0 ? "default" : "secondary"}
            className="ml-1 h-5 min-w-[20px] px-1.5 text-xs"
          >
            {contador.conteo}
          </Badge>
        </button>
      ))}
    </div>
  );
}
